import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  collection, query, where, onSnapshot, getDocs, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, EntityCombobox, Dropdown, DeleteIconButton } from '../components/ui';
import { HeaderSearch, DateRangeButton, ChoiceButton, StatChips, IconPlusSm, IconExportSm } from '../components/HeaderControls';
import PayExpenseBillModal from '../components/PayExpenseBillModal';
import { useSettings } from '../hooks/useSettings';
import { downloadCSV } from '../utils/csv';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  buildEntry, postEntry, entryId, linesPurchaseExpense,
} from '../utils/ledger';
import type {
  PurchaseExpense, PurchaseExpenseTreatment, PurchaseInvoice, InventoryItem, BankAccount, Entity,
} from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
// createdAt is written via serverTimestamp() (a Firestore Timestamp once
// synced back) even though the app's own types declare it as `string` — a
// plain .localeCompare() on it throws and crashes the page the moment two
// rows share the same date and the sort's tie-breaker actually runs. Reads
// either shape (Timestamp instance, plain {seconds}, or an ISO string)
// into sortable millis instead. Mirrors Dashboard.tsx's own toMillis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
// A code is stored in full ("PE-2026-0001"), but the list only shows the
// prefix + last 4 digits to stay compact — same convention as Purchase.tsx's
// lotNoDisplay. The full value is always in a `title` tooltip next to it.
function codeDisplay(code?: string) {
  if (!code) return '—';
  const m = code.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : code.slice(-4);
}
function IdPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', fontFamily: 'var(--font-num)', background: 'var(--primary-light)',
      color: 'var(--primary)', fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 6, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> expense{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

const CATEGORIES = ['Freight', 'Duty / Customs', 'Certification', 'Insurance', 'Bank / Transfer Fees', 'Other'];

// ── Add modal ───────────────────────────────────────────────────────────
function ExpenseModal({ invoices, entities, accounts, onClose }: {
  invoices: PurchaseInvoice[]; entities: Entity[]; accounts: BankAccount[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [category, setCategory] = useState('Freight');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [treatment, setTreatment] = useState<PurchaseExpenseTreatment>('capitalise');
  const [invoiceId, setInvoiceId] = useState('');
  const [vendorEntityId, setVendorEntityId] = useState('');
  const [payNow, setPayNow] = useState(false);
  const [bankAccountId, setBankAccountId] = useState((accounts.find(a => a.isDefault) ?? accounts[0])?.id ?? '');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const linkedInvoice = invoices.find(i => i.id === invoiceId) ?? null;
  const acct = accounts.find(a => a.id === bankAccountId) ?? null;

  async function save() {
    const amt = round2(parseFloat(amount));
    if (!description.trim()) return setError('Enter a short description.');
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (treatment === 'capitalise' && !invoiceId) return setError('Capitalised costs must be linked to a purchase invoice.');
    if (payNow && !bankAccountId) return setError('Choose the account you paid from.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    // For a capitalised cost, pre-read the linked invoice's still-in-stock
    // items so we can spread the amount across them by carat.
    let targets: InventoryItem[] = [];
    if (treatment === 'capitalise') {
      const snap = await getDocs(query(collection(db, 'inventory'), where('purchaseId', '==', invoiceId)));
      targets = snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem))
        .filter(i => i.status === 'in-stock' && (i.carat ?? 0) > 1e-6);
      if (targets.length === 0) {
        setError('None of that invoice’s items are still in stock — capitalising would have nothing to attach to.');
        setSaving(false); return;
      }
    }

    const peRef = doc(collection(db, 'purchaseExpenses'));
    const counterRef = doc(db, 'counters', business.id);
    const currency = acct?.currency ?? settings.currency;
    const prefix = (settings.purchaseExpensePrefix || 'PE').toUpperCase();

    try {
      await runTransaction(db, async tx => {
        const cs = await tx.get(counterRef);
        const accRef = payNow ? doc(db, 'bankAccounts', bankAccountId) : null;
        const accSnap = accRef ? await tx.get(accRef) : null;
        if (accRef && !accSnap?.exists()) throw new Error('That bank account no longer exists.');

        // Re-read the capitalise targets fresh, inside the tx.
        const freshTargets = treatment === 'capitalise'
          ? await Promise.all(targets.map(t => tx.get(doc(db, 'inventory', t.id))))
          : [];

        const n = (cs.exists() ? (cs.data().purchaseExpense ?? 0) : 0) + 1;
        const expenseNo = `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;
        const txRef = payNow ? doc(collection(db, 'bankTransactions')) : null;

        // ── Allocation (capitalise only) ──
        const allocations: { inventoryItemId: string; costingDeltaUSD: number }[] = [];
        if (treatment === 'capitalise') {
          const rows = freshTargets
            .map((s, i) => (s.exists() ? { id: targets[i].id, cur: s.data() as InventoryItem } : null))
            .filter((x): x is { id: string; cur: InventoryItem } => x !== null);
          if (rows.length === 0) throw new Error('Those items are no longer in stock.');
          const totalCt = rows.reduce((sum, r) => sum + (r.cur.carat ?? 0), 0);
          let running = 0;
          rows.forEach((r, i) => {
            const share = i === rows.length - 1
              ? round2(amt - running)
              : round2(amt * ((r.cur.carat ?? 0) / totalCt));
            running = round2(running + share);
            allocations.push({ inventoryItemId: r.id, costingDeltaUSD: share });
            tx.update(doc(db, 'inventory', r.id), {
              costUSD: round2((r.cur.costUSD ?? 0) + share),
              costingUSD: round2((r.cur.costingUSD ?? 0) + share),
              updatedAt: serverTimestamp(),
            });
          });
        }

        tx.set(counterRef, { purchaseExpense: n }, { merge: true });
        tx.set(peRef, stripUndef({
          businessId: business.id, expenseNo, category, description: description.trim(),
          amountUSD: amt, currency, date, treatment,
          invoiceId: invoiceId || undefined, invoiceNo: linkedInvoice?.invoiceNo || undefined,
          vendorEntityId: vendorEntityId || undefined,
          paymentStatus: payNow ? 'paid' : 'unpaid', paidUSD: payNow ? amt : 0,
          bankAccountId: payNow ? acct!.id : undefined,
          bankAccountName: payNow ? acct!.name : undefined,
          paidDate: payNow ? date : undefined,
          bankTransactionId: payNow ? txRef!.id : undefined,
          allocations: treatment === 'capitalise' ? allocations : undefined,
          dueDate: payNow ? undefined : (dueDate || undefined),
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user!.id,
        }));

        if (payNow && accRef && accSnap && txRef) {
          tx.set(txRef, stripUndef({
            businessId: business.id, accountId: acct!.id, accountName: acct!.name,
            currency: acct!.currency, direction: 'out', amount: amt, date,
            description: description.trim(), referenceType: 'purchase_expense',
            referenceId: peRef.id, category,
            createdAt: new Date().toISOString(), createdBy: user!.id,
          }));
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - amt });
        }

        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'purchase_expense', sourceId: peRef.id,
          sourceCollection: 'purchaseExpenses', date, currency, createdBy: user!.id,
          note: `${expenseNo} — ${description.trim()}`,
          lines: linesPurchaseExpense(amt, treatment,
            payNow ? { bankAccountId: acct!.id } : 'accrued', vendorEntityId || undefined),
        }), entryId('purchase_expense', peRef.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the expense.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} hideHead size="lg"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Add</Button>
      </>}>
      <ModalBanner title={description.trim() || category || 'Add Purchase Expense'} eyebrow="Add Purchase Expense" onClose={onClose} />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-row-2">
        <FormField label="Category" required>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Description" required>
        <input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. DHL shipment from Antwerp" />
      </FormField>
      <div className="form-row-2">
        <FormField label={`Amount (${acct?.currency ?? settings.currency})`} required>
          <input type="number" step="0.01" min="0.01" className="no-spinner" value={amount}
            onChange={e => setAmount(e.target.value)} />
        </FormField>
        <FormField label="Paid to (optional)">
          <EntityCombobox
            entities={entities.map(e => ({ id: e.id, companyName: e.companyName }))}
            value={vendorEntityId} onChange={setVendorEntityId} clearLabel="No vendor"
          />
        </FormField>
      </div>

      <FormField label="Treatment" required
        hint={treatment === 'capitalise'
          ? 'Adds to the linked stones’ cost — no effect on Profit until they sell.'
          : 'Hits Profit now as a purchase expense.'}>
        <select value={treatment} onChange={e => setTreatment(e.target.value as PurchaseExpenseTreatment)}>
          <option value="capitalise">Capitalise into stock cost</option>
          <option value="expense">Expense now (to Profit)</option>
        </select>
      </FormField>

      <FormField label={treatment === 'capitalise' ? 'Linked purchase invoice' : 'Linked purchase invoice (optional)'}
        required={treatment === 'capitalise'}>
        {/* Type-to-filter — reuses EntityCombobox (matches by substring on the
            label text), so a long invoice list is searchable by Lot No or amount. */}
        <EntityCombobox
          entities={invoices.map(inv => ({
            id: inv.id,
            companyName: `${codeDisplay(inv.invoiceNo)} · ${(inv.totalCarat ?? 0).toFixed(2)} ct · ${money(inv.totalAmountUSD)}`,
          }))}
          value={invoiceId}
          onChange={setInvoiceId}
          placeholder={treatment === 'capitalise' ? 'Search an invoice…' : 'Search an invoice… (optional)'}
          clearLabel={treatment === 'capitalise' ? undefined : 'None'}
        />
      </FormField>

      <div style={{ marginTop: 6, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={payNow} onChange={e => setPayNow(e.target.checked)} />
          I paid this now
        </label>
        {payNow ? (
          <div style={{ marginTop: 10 }}>
            <FormField label="Paid from" required>
              <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
                <option value="">Select account…</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
              </select>
            </FormField>
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            <FormField label="Due date (optional)">
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </FormField>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function PurchaseExpensesPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [expenses, setExpenses] = useState<PurchaseExpense[]>([]);
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [treatFilter, setTreatFilter] = useState<'all' | PurchaseExpenseTreatment>('all');

  const [showAdd, setShowAdd] = useState(false);
  const [confirm, setConfirm] = useState<PurchaseExpense | null>(null);
  const [payTarget, setPayTarget] = useState<PurchaseExpense | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'purchaseExpenses'),
      snap => { setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseExpense))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'invoices'),
      snap => setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))
        .sort((a, b) => (b.purchaseDate ?? '').localeCompare(a.purchaseDate ?? ''))), () => {});
    const u3 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    const u4 = onSnapshot(collection(db, 'bankAccounts'),
      snap => setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))), () => {});
    return () => { u1(); u2(); u3(); u4(); };
  }, [business]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const entityName = (id?: string) => (id ? entities.find(e => e.id === id)?.companyName ?? '—' : '—');

  const rows = useMemo(() => {
    if (!window) return [];
    return expenses
      .filter(e => inWindow(e.date, window))
      .filter(e => treatFilter === 'all' || e.treatment === treatFilter)
      .filter(e => {
        const q = searchQ.trim().toLowerCase();
        return !q || [e.expenseNo, e.category, e.description, e.invoiceNo, e.vendorEntityId ? entityName(e.vendorEntityId) : '']
          .some(v => (v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => b.date.localeCompare(a.date) || toMillis(b.createdAt) - toMillis(a.createdAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, window, treatFilter, searchQ, entities]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, treatFilter, searchQ]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Print covers every filtered row, not just this page (same as the other list pages).
  // globalThis, not window: `window` here is the page's date range.
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    globalThis.addEventListener('afterprint', after);
    requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) globalThis.print(); }); });
    return () => { cancelled = true; globalThis.removeEventListener('afterprint', after); };
  }, [printAll]);

  function handleExportCSV() {
    downloadCSV('purchase-expenses',
      ['Date', 'Ref', 'Category', 'Description', 'Vendor', 'Invoice', 'Treatment', 'Amount', 'Status'],
      rows.map(e => [e.date, e.expenseNo, e.category, e.description, e.vendorEntityId ? entityName(e.vendorEntityId) : '', e.invoiceNo ?? '', e.treatment === 'capitalise' ? 'Capitalised' : 'Expensed', (e.amountUSD ?? 0).toFixed(2), e.paymentStatus ?? '']),
    );
  }

  const kpi = useMemo(() => {
    const inW = window ? expenses.filter(e => inWindow(e.date, window)) : [];
    const cap = inW.filter(e => e.treatment === 'capitalise').reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    const exp = inW.filter(e => e.treatment === 'expense').reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    const unpaid = expenses.filter(e => e.paymentStatus === 'unpaid').reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    return { cap, exp, unpaid };
  }, [expenses, window]);

  async function doDelete(e: PurchaseExpense) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      await runTransaction(db, async tx => {
        const peRef = doc(db, 'purchaseExpenses', e.id);
        const accRef = e.paymentStatus === 'paid' && e.bankAccountId ? doc(db, 'bankAccounts', e.bankAccountId) : null;
        const allocSnaps = await Promise.all((e.allocations ?? []).map(a => tx.get(doc(db, 'inventory', a.inventoryItemId))));
        const accSnap = accRef ? await tx.get(accRef) : null;

        (e.allocations ?? []).forEach((a, i) => {
          const s = allocSnaps[i];
          if (!s.exists()) return;
          const cur = s.data() as InventoryItem;
          tx.update(doc(db, 'inventory', a.inventoryItemId), {
            costUSD: round2((cur.costUSD ?? 0) - a.costingDeltaUSD),
            costingUSD: round2((cur.costingUSD ?? 0) - a.costingDeltaUSD),
            updatedAt: serverTimestamp(),
          });
        });
        if (accRef && accSnap?.exists()) {
          tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance + (e.amountUSD ?? 0) });
        }
        if (e.bankTransactionId) tx.delete(doc(db, 'bankTransactions', e.bankTransactionId));
        tx.delete(doc(db, 'journalEntries', entryId('purchase_expense', e.id)));
        tx.delete(peRef);
      });
    } finally { setBusy(false); setConfirm(null); }
  }

  return (
    <AppLayout
      title="Purchase Expenses"
      subtitle="Freight, duty, certification and the like — capitalise them into stock cost or expense them straight to Profit."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Ref, category, description…" label="Search purchase expenses" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        <ChoiceButton<'all' | 'capitalise' | 'expense'>
          label="Treatment" value={treatFilter} onChange={setTreatFilter} defaultKey="all"
          options={[{ key: 'all', label: 'All' }, { key: 'capitalise', label: 'Capitalised' }, { key: 'expense', label: 'Expensed' }]}
        />
        {(searchQ || dateKey !== 'this-month' || treatFilter !== 'all') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); setTreatFilter('all'); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <div className="tb-group">
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setShowAdd(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>Add Expense</Button>
      </>}
    >
      <StatChips items={[
        { label: 'Capitalised', value: money(kpi.cap) },
        { label: 'Expensed', value: money(kpi.exp) },
        { label: 'Bills to pay', value: money(kpi.unpaid), tone: kpi.unpaid > 0 ? 'danger' : undefined },
      ]} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No purchase expenses here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Add one with the button above.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 120 }}>Ref</th>
                  <th style={{ width: 140 }}>Category</th>
                  <th>Description</th>
                  <th style={{ width: 120 }}>Invoice</th>
                  <th style={{ width: 110 }}>Treatment</th>
                  <th style={{ textAlign: 'right', width: 120 }}>Amount</th>
                  <th style={{ width: 80 }}>Status</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(printAll ? rows : pagedRows).map(e => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(e.date)}</td>
                    <td title={e.expenseNo}><IdPill>{codeDisplay(e.expenseNo)}</IdPill></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{e.category}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {e.description}
                      {e.vendorEntityId && <span style={{ color: 'var(--text-3)' }}> · {entityName(e.vendorEntityId)}</span>}
                    </td>
                    <td>
                      {e.invoiceNo ? <span title={e.invoiceNo}><IdPill>{codeDisplay(e.invoiceNo)}</IdPill></span> : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: e.treatment === 'capitalise' ? 'var(--primary-light)' : 'var(--surface-2)',
                        color: e.treatment === 'capitalise' ? 'var(--primary)' : 'var(--text-3)',
                      }}>{e.treatment === 'capitalise' ? 'Capitalised' : 'Expensed'}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', textAlign: 'right', fontWeight: 600 }}>
                      {money(e.amountUSD)}
                    </td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: e.paymentStatus === 'paid' ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: e.paymentStatus === 'paid' ? 'var(--success)' : 'var(--danger)',
                      }}>{e.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}</span>
                    </td>
                    <td className="col-actions">
                      {e.paymentStatus === 'unpaid' && (
                        <Button variant="secondary" size="sm" onClick={() => setPayTarget(e)}>Pay</Button>
                      )}
                      <DeleteIconButton onClick={() => setConfirm(e)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {showAdd && (
        <ExpenseModal invoices={invoices} entities={entities} accounts={accounts} onClose={() => setShowAdd(false)} />
      )}
      {payTarget && (
        <PayExpenseBillModal bill={{ kind: 'purchase', expense: payTarget }} accounts={accounts} onClose={() => setPayTarget(null)} />
      )}
      {confirm && (
        <ConfirmModal
          open
          title="Delete this purchase expense?"
          message={confirm.treatment === 'capitalise'
            ? `${confirm.expenseNo} (${money(confirm.amountUSD)}) will be removed and its cost taken back off the linked stones. Any linked bank movement is reversed.`
            : `${confirm.expenseNo} (${money(confirm.amountUSD)}) will be removed and its Profit effect reversed. Any linked bank movement is reversed.`}
          confirmLabel="Delete"
          loading={busy}
          onConfirm={() => doDelete(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </AppLayout>
  );
}
